import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import type { FacetOption } from '@nest-recruit/shared';
import { MenuItem, Popover, usePopover } from '../../../components/Popover';
import { formatNumber } from '../../../lib/format';
import styles from './FacetFilterButton.module.css';

/**
 * One always-visible, single-choice filter in the question bank's toolbar (Redesign Group 6,
 * Question-bank.dc.html `.fbtn`): "Difficulty: Any difficulty", opening its options beside it,
 * each with how many questions it would show given the other filters. Choosing closes it.
 */

interface FacetFilterButtonProps {
  readonly label: string;
  /** "Any kind", "Any skill"… — the choice that clears this filter. */
  readonly anyLabel: string;
  /** Its count, or undefined until the counts have loaded. */
  readonly anyCount: number | undefined;
  readonly options: readonly (Omit<FacetOption, 'count'> & { readonly count: number | undefined })[];
  readonly value: string | undefined;
  readonly onChange: (value: string | undefined) => void;
}

export function FacetFilterButton({ label, anyLabel, anyCount, options, value, onChange }: FacetFilterButtonProps): ReactNode {
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const chosen = options.find((option) => option.value === value);

  function pick(next: string | undefined): void {
    onChange(next);
    close();
  }

  return (
    <>
      <button ref={triggerRef} type="button" className={styles.button} data-active={chosen !== undefined || undefined} aria-haspopup="menu" aria-expanded={isOpen} onClick={toggle}>
        {label}: <strong className={styles.value}>{chosen?.label ?? anyLabel}</strong>
        <ChevronDown size={14} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} label={label}>
        <div className={styles.options}>
          <Option label={anyLabel} count={anyCount} isOn={chosen === undefined} onPick={() => pick(undefined)} />
          {options.map((option) => (
            <Option key={option.value} label={option.label} count={option.count} isOn={option.value === value} onPick={() => pick(option.value)} />
          ))}
        </div>
      </Popover>
    </>
  );
}

function Option({ label, count, isOn, onPick }: { readonly label: string; readonly count: number | undefined; readonly isOn: boolean; readonly onPick: () => void }): ReactNode {
  return (
    <MenuItem
      isSelected={isOn}
      onClick={onPick}
      meta={
        <span className={styles.meta}>
          {count !== undefined && <span className={styles.count}>{formatNumber(count)}</span>}
          {isOn && <Check size={14} aria-hidden />}
        </span>
      }
    >
      {label}
    </MenuItem>
  );
}
